import { useState, useEffect, useContext, useCallback  } from 'react';
import api from './api/axiosInstance';
import Sidebar from './Sidebar.js';
import { Card, Row, Col, Container, Button, Modal, Alert, Form } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import TicketBasicInfo from './TicketBasicInfo';
import TicketAddDetails from './TicketAddDetails';
import TicketAddActivity from './TicketAddActivity';
import { UserContext } from './UserContext';
import { TicketCountContext } from './TicketCountContext';
import Breadcrumb from './Breadcrumb.js';

const TicketEdit = () => {
    const { id } = useParams();
    const { user } = useContext(UserContext);
    const [errors] = useState({});
    const [modalMode, setModalMode] = useState('archive');
    const [showArchiveModal, setShowArchiveModal] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);

    const { ticketCount, refreshTicketCount } = useContext(TicketCountContext);

    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        priority: '',
        ticketClass: '',
        status: '',
        owner: '',
        businessPurpose: '',
    });

    const [ticket, setTicket] = useState(null);
    
    const fetchTicketById = useCallback(async () => {
        try {
            const response = await api.get(`/api/tickets/${id}`);
    
            const data = response.data;
    
            setFormData({
                priority: data.priority || '',
                ticketClass: data.ticketClass || '',
                status: data.status || '',
                owner: data.owner || '',
                businessPurpose: data.businessPurpose || '',
                activity: data.activity || [],
                attachments: data.attachments || [],
            });
    
            setTicket(data);
        } catch (error) {
            console.error('Error fetching ticket:', error);
        }
    }, [id]);
    

    useEffect(() => {
        if (user && id) fetchTicketById();
    }, [user, id, fetchTicketById]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        console.log('handleChange called with:', name, value);
    
        // Check if the incoming change is for the country object and handle accordingly
        if (name === 'country' && typeof value === 'object') {
            setFormData(prevData => ({
                ...prevData,
                country: { ...value }  // Update the country object properly
            }));
        } else {
            setFormData(prevData => ({
                ...prevData,
                [name]: value  // This assumes value is always correct
            }));
        }
    };

    const handleGeneralSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await api.put(`/api/tickets/${id}`, formData);
            console.log('Updated ticket:', response.data);
        } catch (error) {
            console.error('Error updating ticket:', error);
        }
    };

    const handleArchiveTicket = async () => {
        try {
            await api.put(`/api/tickets/${id}`, 
                { ...formData, status: 'Archived' }
            );
            setTicket(prev => ({ ...prev, status: 'Archived' }));
            setModalMode('archive');
            refreshTicketCount();
            setShowArchiveModal(true);
        } catch (error) {
            console.error('Error archiving ticket:', error);
            alert('An error occurred while archiving the ticket.');
        }
    };

    const handleUnarchiveTicket = async () => {
        try {
            await api.put(`/api/tickets/${id}`, 
                { ...formData, status: 'Open' }
            );
            setTicket(prev => ({ ...prev, status: 'Open' }));
            setModalMode('unarchive'); // ✅ Set modal mode
            setShowArchiveModal(true);
        } catch (error) {
            console.error('Error unarchiving ticket:', error);
            alert('An error occurred while unarchiving the ticket.');
        }
    };

    return (
        <>
            <Sidebar ticketCount={ticketCount} />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            {showErrorMessage && (
                                <Alert variant="danger" onClose={() => setShowErrorMessage(false)} dismissible>
                                    <p><strong>Error</strong> - There are items that require your attention</p>
                                </Alert>
                            )}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>
                    <Row>
                        {/* Main Content */}
                        <Col xs={12}>
                            <Card>
                                <Card.Body>
                                    <Row>
                                        <Col>
                                            <h1>Edit Ticket</h1>
                                        </Col>
                                    </Row>
                                    <>  
                                        <Row>
                                            <Col md={12}>
                                                <TicketBasicInfo
                                                    formData={formData}
                                                    handleChange={handleChange}
                                                    errors={errors}
                                                    ticket={ticket}
                                                />
                                            </Col>
                                        </Row>
                                        <Row>
                                        <Col md={6}>
                                            <Form onSubmit={handleGeneralSubmit}>
                                                <TicketAddDetails
                                                    formData={formData}
                                                    handleChange={handleChange}
                                                    errors={errors}
                                                    isEditPage={true}
                                                    handleGeneralSubmit={handleGeneralSubmit}
                                                />
                                            </Form>
                                        </Col>
                                        <Col md={6}>
                                            <TicketAddActivity
                                                formData={formData}
                                                handleChange={handleChange}
                                                errors={errors}
                                                ticketId={id}
                                                setFormData={setFormData}
                                                refreshTicket={fetchTicketById}
                                            />
                                        </Col>
                                        </Row>
                                        <Row className="text-end mb-3">
                                            <Col md={12}>
                                                {ticket && ticket.status !== 'Archived' && (
                                                    <Button variant="outline-secondary" className="me-3" onClick={handleArchiveTicket}>
                                                        Archive
                                                    </Button>
                                                )}
                                                {ticket && ticket.status === 'Archived' && (
                                                    <Button variant="outline-secondary" className="me-3" onClick={handleUnarchiveTicket}>
                                                        Unarchive
                                                    </Button>
                                                )}
                                                <Button variant="primary" onClick={() => navigate('/ticket-list')}>
                                                    Close
                                                </Button>
                                            </Col>
                                        </Row>
                                    </>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </main>
                                    
            {/* Archive Modal */}
            <Modal show={showArchiveModal} onHide={() => setShowArchiveModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalMode === 'archive' ? 'Ticket Archived' : 'Ticket Unarchived'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalMode === 'archive' ? (
                        <p>
                            This ticket has been archived.&nbsp;
                            <span
                                role="button"
                                style={{ color: '$primary', textDecoration: 'underline', cursor: 'pointer' }}
                                onClick={handleUnarchiveTicket}
                            >
                                Undo
                            </span>
                        </p>
                    ) : (
                        <p>This ticket has been restored from archive.</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => navigate('/ticket-list')}>
                        Go to Tickets
                    </Button>
                </Modal.Footer>
            </Modal>

        </>
    );
};

export default TicketEdit;
